import { useState, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { EmptyState, ErrorNotice, Notice, Spinner, formatDate, formatMoney } from '../components/ui';
import type { ImportPreview, ImportRow } from '../types';

/**
 * Matching a bank statement against who owes what.
 *
 * Nothing here imports anything on its own. The statement is parsed, matches
 * are proposed with a reason attached, and every row the treasurer leaves
 * unticked is simply not recorded. Confident matches arrive pre-ticked to
 * save the clicking; everything else waits to be looked at.
 */
export function StatementImportPage() {
  const navigate = useNavigate();
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [chosen, setChosen] = useState<Record<string, number | null>>({});
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ recorded: number; skipped: { reason: string }[] } | null>(null);

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setResult(null);
    setBusy(true);
    setFileName(file.name);
    try {
      const csv = await file.text();
      const result = await api.post<ImportPreview>('/statement-import/preview', { csv });
      setPreview(result);
      // Pre-tick only what the server considered confident. Everything else
      // is an explicit decision.
      const initial: Record<string, number | null> = {};
      for (const row of result.rows) {
        const best = row.matches[0];
        initial[row.fingerprint] =
          !row.already_imported && best && best.confidence >= result.confident_at
            ? best.subscription_id
            : null;
      }
      setChosen(initial);
    } catch (failure) {
      setError(failure);
      setPreview(null);
    } finally {
      setBusy(false);
      event.target.value = '';
    }
  }

  const selected = preview?.rows.filter((row) => chosen[row.fingerprint] != null) ?? [];

  async function confirm() {
    setError(null);
    setBusy(true);
    try {
      const outcome = await api.post<{ recorded: number; skipped: { reason: string }[] }>(
        '/statement-import/confirm',
        {
          rows: selected.map((row) => ({
            subscription_id: chosen[row.fingerprint],
            fingerprint: row.fingerprint,
            amount: row.amount,
            date: row.date,
            reference: row.reference,
          })),
        },
      );
      setResult(outcome);
      setPreview(null);
      setChosen({});
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p className="muted small">
        <Link to="/subscriptions">← Back to subscriptions</Link>
      </p>

      <div className="page-head">
        <h1>Import a bank statement</h1>
      </div>

      <ErrorNotice error={error} />

      {result !== null ? (
        <Notice kind="success">
          {result.recorded} payment{result.recorded === 1 ? '' : 's'} recorded.
          {result.skipped.length > 0
            ? ` ${result.skipped.length} skipped: ${result.skipped[0].reason}`
            : ''}{' '}
          <button type="button" className="link" onClick={() => navigate('/subscriptions')}>
            Back to the list
          </button>
        </Notice>
      ) : null}

      {preview === null ? (
        <div className="card">
          <h2>Choose the file</h2>
          <p className="muted small">
            Export your statement from online banking as CSV and pick it here. Nothing is
            recorded until you have looked at the matches and pressed Import — this step only
            reads the file.
          </p>
          <div className="field">
            <label htmlFor="statement-file">Statement file</label>
            <input id="statement-file" type="file" accept=".csv,text/csv,text/plain" onChange={(e) => void onFile(e)} />
          </div>
          {busy ? <Spinner label="Reading the statement" /> : null}
        </div>
      ) : null}

      {preview !== null ? (
        <>
          {preview.columns_inferred ? (
            <Notice kind="info">
              That file had no column headings, so which column is which was worked out from the
              data. Check the dates and amounts below look right before importing — if anything
              is off, re-export from your bank with headings included.
            </Notice>
          ) : null}

          <div className="card">
            <p className="muted small">
              {fileName} — {preview.summary.rows} money-in row
              {preview.summary.rows === 1 ? '' : 's'}, {preview.summary.confident} matched
              confidently
              {preview.summary.already_imported > 0
                ? `, ${preview.summary.already_imported} already recorded`
                : ''}
              . Check each one before importing.
            </p>
          </div>

          {preview.rows.length === 0 ? (
            <EmptyState title="Nothing to import" />
          ) : (
            <table className="members">
              <thead>
                <tr>
                  <th scope="col">From the statement</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Record against</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <ImportRowView
                    key={row.fingerprint}
                    row={row}
                    confidentAt={preview.confident_at}
                    chosen={chosen[row.fingerprint] ?? null}
                    onChoose={(subscriptionId) =>
                      setChosen((previous) => ({ ...previous, [row.fingerprint]: subscriptionId }))
                    }
                  />
                ))}
              </tbody>
            </table>
          )}

          <div className="actions import-actions">
            <button type="button" className="primary" disabled={busy || selected.length === 0} onClick={() => void confirm()}>
              {busy
                ? 'Importing…'
                : `Import ${selected.length} payment${selected.length === 1 ? '' : 's'}`}
            </button>
            <button type="button" disabled={busy} onClick={() => { setPreview(null); setFileName(null); }}>
              Start again
            </button>
          </div>
        </>
      ) : null}
    </>
  );
}

function ImportRowView({
  row,
  confidentAt,
  chosen,
  onChoose,
}: {
  row: ImportRow;
  confidentAt: number;
  chosen: number | null;
  onChoose: (subscriptionId: number | null) => void;
}) {
  if (row.already_imported) {
    return (
      <tr className="row-muted">
        <td data-label="From the statement">
          <span className="muted">{row.reference || '(no reference)'}</span>
          <span className="muted block small">{formatDate(row.date)}</span>
        </td>
        <td data-label="Amount" className="numeric muted">
          {formatMoney(row.amount)}
        </td>
        <td data-label="Record against">
          <span className="muted">Already recorded — skipping.</span>
        </td>
      </tr>
    );
  }

  const best = row.matches[0];

  return (
    <tr>
      <td data-label="From the statement">
        {row.reference || <span className="muted">(no reference)</span>}
        <span className="muted block small">{formatDate(row.date)}</span>
      </td>
      <td data-label="Amount" className="numeric">
        {formatMoney(row.amount)}
      </td>
      <td data-label="Record against">
        {row.matches.length === 0 ? (
          <span className="muted">
            No likely match. Record it by hand from the subscriptions list if you know who it is.
          </span>
        ) : (
          <>
            <select
              value={chosen ?? ''}
              aria-label={`Who the payment of ${row.amount} on ${row.date} belongs to`}
              onChange={(e) => onChoose(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">Do not import this row</option>
              {row.matches.map((match) => (
                <option key={match.subscription_id} value={match.subscription_id}>
                  {match.name} — {formatMoney(match.outstanding)} outstanding ({match.confidence}%)
                </option>
              ))}
            </select>
            {best ? (
              <span className={`muted block small ${best.confidence >= confidentAt ? 'match-confident' : ''}`}>
                {best.reason}
              </span>
            ) : null}
          </>
        )}
      </td>
    </tr>
  );
}
